import { Component, computed, inject, input, signal } from '@angular/core';
import { MD_DIALOG_REF, MDDialogModule, MDDialogRef, MDIconComponent, MDTagComponent } from '@melodic-ui/components';
import { GradeTagComponent } from '../../grade-tag/grade-tag.component';
import { IndicatorType } from '@melodic-ui/types';
import { AccountStateService } from '@lb/state/account-state.service';

export interface ISaveReviewGradeItem {
	categoryName: string;
	gradeName: string | null;
	gradeColor: IndicatorType;
}

export interface ISaveReviewResult {
	attendanceStatusID: number | null;
	generateInvoice: boolean;
}

@Component({
	selector: 'lb-save-review-dialog',
	standalone: true,
	imports: [MDDialogModule, MDIconComponent, MDTagComponent, GradeTagComponent],
	templateUrl: './save-review-dialog.component.html',
	styleUrl: './save-review-dialog.component.scss'
})
export class SaveReviewDialogComponent {
	private readonly dialogRef = inject<MDDialogRef>(MD_DIALOG_REF);
	private readonly accountState = inject(AccountStateService);

	gradeItems = input<ISaveReviewGradeItem[]>([]);
	objectiveNames = input<string[]>([]);
	projectMilestoneName = input<string | null>(null);
	homeworkCategories = input<string[]>([]);
	homeworkNoGrade = input<boolean>(false);
	currentAttendanceStatusID = input<number | null>(null);
	canTakeAttendance = input<boolean>(false);
	hasDetailChanges = input<boolean>(false);
	/** Mastery lessons don't log grades, objectives, homework, or project progress — only attendance is reviewed. */
	isMastery = input<boolean>(false);
	/** Multi-attendance (chirp room): the roster is what gets recorded — summarized below instead of a single value. */
	isMultiAttendance = input<boolean>(false);
	presentCount = input<number>(0);
	absentCount = input<number>(0);

	generateInvoice = signal<boolean>(true);

	isAdmin = this.accountState.isAdmin;

	/** Mastery lessons review attendance only, so every detail section is suppressed. */
	showDetailSections = computed(() => !this.isMastery() && this.hasDetailChanges());
	hasGrades = computed(() => this.gradeItems().some((g) => g.gradeName !== null));
	hasObjectives = computed(() => this.objectiveNames().length > 0);
	showHomeworkCategories = computed(() => !this.isMastery() && this.homeworkCategories().length > 0);
	isAttendanceTaken = computed(() => this.currentAttendanceStatusID() != null);

	confirm(): void {
		const result: ISaveReviewResult = {
			// Always set to "Present" since this dialog is only shown when there are changes to save, and saving also takes attendance if possible.
			attendanceStatusID: this.canTakeAttendance() ? 1 : null,
			generateInvoice: this.isAdmin() ? this.generateInvoice() : true
		};
		this.dialogRef.close(result);
	}

	close(): void {
		this.dialogRef.close(null);
	}
}
